<template>
  <div class="ds-sidebar-form">
    <!-- Workspace -->
    <div v-if="showWorkspace" class="ds-field">
      <label class="ds-label">Project</label>
      <div class="ds-input">
        <QuickAddWorkspace
          :value="workspace"
          @input="$emit('update:workspace', $event)"
          :profile="profile"
          size="sm"
        />
      </div>
    </div>

    <!-- Dimensions -->
    <div class="ds-field">
      <label class="ds-label">Size</label>
      <div class="ds-input">
        <div class="d-flex align-items-center">
          <b-form-input
            :value="dimensions.width"
            @input="updateDimension('width', $event)"
            type="number"
            placeholder="W"
            size="sm"
            :state="getFieldError('config_data.width', 'width') ? false : null"
          ></b-form-input>
          <span class="mx-1 small text-muted">&times;</span>
          <b-form-input
            :value="dimensions.height"
            @input="updateDimension('height', $event)"
            type="number"
            placeholder="H"
            size="sm"
            :state="getFieldError('config_data.height', 'height') ? false : null"
          ></b-form-input>
        </div>
        <div v-if="getFieldError('config_data.width', 'width')" class="ds-error">{{ getFieldError('config_data.width', 'width') }}</div>
        <div v-if="getFieldError('config_data.height', 'height')" class="ds-error">{{ getFieldError('config_data.height', 'height') }}</div>
      </div>
    </div>

    <!-- Substrate -->
    <div class="ds-field">
      <label class="ds-label">Substrate</label>
      <div class="ds-input">
        <b-form-select :value="selectedSubstrate" @input="onSubstrateChange($event)" :options="substrateOptions" size="sm">
          <template #first>
            <b-form-select-option :value="null" disabled>-- Select --</b-form-select-option>
          </template>
        </b-form-select>
      </div>
    </div>

    <!-- Options -->
    <template v-if="selectedSubstrate">
      <div v-if="loadingOptions" class="text-center py-2">
        <b-spinner small label="Loading..."></b-spinner>
      </div>
      <div v-else-if="availableOptions.length === 0" class="small text-muted py-1">
        No options available.
      </div>
      <template v-else>
        <hr class="my-2">
        <template v-for="(options, category) in groupedOptions">
          <!-- Subcategory filter -->
          <div v-if="getSubcategories(options).length > 0" :key="category + '-sub'" class="ds-field">
            <label class="ds-label">{{ category }} Family</label>
            <div class="ds-input">
              <b-form-select
                :value="selectedSubcategories[category]"
                @input="updateSubcategory(category, $event)"
                :options="getSubcategories(options)"
                size="sm"
              >
                <template #first>
                  <b-form-select-option :value="null">All</b-form-select-option>
                </template>
              </b-form-select>
            </div>
          </div>

          <!-- Option select -->
          <div :key="category" class="ds-field">
            <label class="ds-label" :title="category">{{ category }}</label>
            <div class="ds-input">
              <b-form-select
                :value="selectedOptions[category]"
                @input="updateOption(category, $event)"
                :options="getCategoryOptionsFiltered(options, category)"
                :state="getOptionError(category) ? false : null"
                size="sm"
              >
                <template #first>
                  <b-form-select-option :value="null">-- Select --</b-form-select-option>
                </template>
              </b-form-select>
              <div v-if="getOptionError(category)" class="ds-error">{{ getOptionError(category) }}</div>
            </div>
          </div>

          <!-- Frame images (below Frame select) -->
          <div
            v-if="isFrameCategory(category) && computedFrameData"
            :key="category + '-images'"
            class="ds-frame-images"
          >
            <label class="ds-label small text-muted">Frame images</label>
            <div class="d-flex flex-wrap gap-2 mt-1">
              <div
                v-for="key in frameImageKeys"
                :key="key"
                v-show="computedFrameData[key]"
                class="ds-frame-img"
              >
                <img :src="computedFrameData[key]" :alt="key" class="rounded" />
                <small class="d-block text-muted mt-1">{{ key }}</small>
              </div>
            </div>
          </div>
        </template>
      </template>
    </template>
  </div>
</template>

<script>
import { groupBy } from 'lodash'
import QuickAddWorkspace from '@/components/QuickAddWorkspace.vue'

export default {
  name: 'DesignStudioItemForm',
  components: {
    QuickAddWorkspace,
  },
  props: {
    substrates: { type: Array, default: () => [] },
    selectedSubstrate: { default: null },
    availableOptions: { type: Array, default: () => [] },
    selectedOptions: { type: Object, default: () => ({}) },
    selectedSubcategories: { type: Object, default: () => ({}) },
    dimensions: { type: Object, default: () => ({ width: '', height: '' }) },
    workspace: { type: Object, default: null },
    profile: { type: Object, default: null },
    loadingSubstrates: { type: Boolean, default: false },
    loadingOptions: { type: Boolean, default: false },
    validationErrors: { type: Object, default: () => ({}) },
    showWorkspace: { type: Boolean, default: true },
  },
  data() {
    return {
      frameImageKeys: ['front_image', 'side_image', 'top_image'],
    }
  },
  computed: {
    substrateOptions() {
      return this.substrates.map(s => ({
        value: s.id,
        text: s.name,
      }))
    },
    groupedOptions() {
      return groupBy(this.availableOptions, 'name')
    },
    currentSubstrateName() {
      const sub = this.substrates.find(s => s.id === this.selectedSubstrate)
      return sub ? sub.name : ''
    },
    /**
     * Frame extra_data from the selected Frame option.
     */
    computedFrameData() {
      for (const [category, optionId] of Object.entries(this.selectedOptions)) {
        if (!optionId) continue
        if (!/frame/i.test(category)) continue
        const opt = this.availableOptions.find(o => o.id === optionId)
        if (opt && opt.extra_data && opt.extra_data.top_image) {
          return opt.extra_data
        }
      }
      return null
    },
    /** Number of mats (1, 2, or 3) from "Number of mats" option */
    computedNumberOfMats() {
      for (const [category, optionId] of Object.entries(this.selectedOptions)) {
        if (!optionId) continue
        if (!/number\s*of\s*mats/i.test(category)) continue
        const opt = this.availableOptions.find(o => o.id === optionId)
        if (!opt) continue
        const v = parseInt(opt.value ?? opt.num_value ?? opt.char_value, 10)
        return isNaN(v) ? 1 : Math.max(1, Math.min(3, v))
      }
      return 1
    },
    /** Single-mat backwards compatibility */
    computedMatData() {
      const arr = this.computedMatDataArray
      return arr && arr.length > 0 ? arr[0] : null
    },
    /** Array of mat data for Mat 1, Mat 2, Mat 3 */
    computedMatDataArray() {
      const numMats = this.computedNumberOfMats
      const result = []
      for (const n of [1, 2, 3]) {
        if (n > numMats) break
        const re = new RegExp(`^Mat\\s*${n}$`, 'i')
        for (const [category, optionId] of Object.entries(this.selectedOptions)) {
          if (!optionId || !re.test(category)) continue
          const opt = this.availableOptions.find(o => o.id === optionId)
          if (opt?.extra_data?.color) {
            result.push({ color: opt.extra_data.color })
          }
          break
        }
      }
      return result
    },
    /** Per-side mat widths from options */
    computedMatWidth() {
      const arr = this.computedMatDataArray
      if (!arr?.length) return { top: 0, bottom: 0, left: 0, right: 0 }
      const perMat = this.computedMatWidthsArray
      if (perMat?.length === 1) return perMat[0]
      if (perMat?.length) {
        const sum = perMat.reduce((a, w) => ({
          top: a.top + w.top, bottom: a.bottom + w.bottom,
          left: a.left + w.left, right: a.right + w.right,
        }), { top: 0, bottom: 0, left: 0, right: 0 })
        const n = perMat.length
        const revealPerSide = (n - 1) * 0.25
        return {
          top: sum.top + revealPerSide,
          bottom: sum.bottom + revealPerSide,
          left: sum.left + revealPerSide,
          right: sum.right + revealPerSide,
        }
      }
      return { top: 0, bottom: 0, left: 0, right: 0 }
    },
    /** Per-mat widths array */
    computedMatWidthsArray() {
      const matDataArr = this.computedMatDataArray
      if (!matDataArr?.length) return []
      const total = { top: 2, bottom: 2, left: 2, right: 2 }
      for (const [category, optionId] of Object.entries(this.selectedOptions)) {
        if (!optionId) continue
        if (!/mat\s*width/i.test(category)) continue
        const opt = this.availableOptions.find(o => o.id === optionId)
        if (!opt) continue
        const val = parseFloat(opt.value)
        if (isNaN(val)) continue
        const lower = category.toLowerCase()
        if (lower.includes('top')) total.top = val
        else if (lower.includes('bottom')) total.bottom = val
        else if (lower.includes('left')) total.left = val
        else if (lower.includes('right')) total.right = val
        else {
          total.top = total.bottom = total.left = total.right = val
        }
      }
      return matDataArr.map((_, i) =>
        i === 0
          ? { top: total.top, bottom: total.bottom, left: total.left, right: total.right }
          : { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 }
      )
    },
    /** Canvas mount style */
    computedCanvasMount() {
      for (const [category, optionId] of Object.entries(this.selectedOptions)) {
        if (!optionId) continue
        if (!/canvas\s*mount/i.test(category)) continue
        const opt = this.availableOptions.find(o => o.id === optionId)
        if (opt) {
          const val = opt.value != null ? String(opt.value) : opt.name || ''
          return val || null
        }
      }
      return null
    },
    /** Float frame extra_data */
    computedFloatFrameData() {
      for (const [category, optionId] of Object.entries(this.selectedOptions)) {
        if (!optionId) continue
        if (!/float\s*frame/i.test(category)) continue
        const opt = this.availableOptions.find(o => o.id === optionId)
        if (opt && opt.extra_data) return opt.extra_data
      }
      return null
    },
    /** Stretcher depth in inches (Canvas) */
    computedStretcherDepth() {
      for (const [category, optionId] of Object.entries(this.selectedOptions)) {
        if (!optionId) continue
        if (!/stretcher/i.test(category)) continue
        const opt = this.availableOptions.find(o => o.id === optionId)
        if (!opt) continue
        const val = opt.value != null ? String(opt.value) : opt.name || ''
        const parsed = parseFloat(val.replace(/["']/g, ''))
        return isNaN(parsed) ? null : parsed
      }
      return null
    },
    /** Substrate back data */
    computedSubstrateBackData() {
      const sub = String(this.currentSubstrateName || '').toLowerCase()
      if (!sub) return null

      const getOptionValue = (pattern) => {
        for (const [category, optionId] of Object.entries(this.selectedOptions)) {
          if (!optionId) continue
          if (!pattern.test(category)) continue
          const opt = this.availableOptions.find(o => o.id === optionId)
          return opt ? (opt.value != null ? String(opt.value) : opt.name || '') : null
        }
        return null
      }
      const parseInches = (val) => {
        if (val == null) return null
        const s = String(val).replace(/["']/g, '').trim()
        const frac = s.match(/^(\d+)\s*\/\s*(\d+)/)
        if (frac) return Number(frac[1]) / Number(frac[2])
        const parsed = parseFloat(s)
        return isNaN(parsed) ? null : parsed
      }

      if (/aluminum/i.test(sub)) {
        const material = getOptionValue(/aluminum\s*material/i) || ''
        const isWhite = /white/i.test(material)
        const border = parseInches(getOptionValue(/border/i)) ?? 0
        return { depth: 0.25, color: isWhite ? 0xffffff : 0xc0c0c0, roughness: 0.4, metalness: 0.6, hasSides: true, border }
      }
      if (/acrylic/i.test(sub)) {
        const acrylicVal = getOptionValue(/acrylic\s*substrate/i)
        const depth = parseInches(acrylicVal) ?? 0.25
        const border = parseInches(getOptionValue(/border/i)) ?? 0
        const colorFromName = (val) => {
          if (!val) return null
          const s = String(val).toLowerCase()
          if (/black/i.test(s)) return 0x2a2a2a
          if (/white/i.test(s)) return 0xffffff
          if (/clear|transparent/i.test(s)) return 0xb8d4e3
          if (/silver|grey|gray/i.test(s)) return 0xc0c0c0
          if (/bronze/i.test(s)) return 0xcd7f32
          if (/gold/i.test(s)) return 0xd4af37
          return null
        }
        return { depth, color: colorFromName(acrylicVal) ?? 0xb8d4e3, roughness: 0.1, metalness: 0.3, hasSides: true, border }
      }
      if (/acoustic/i.test(sub)) return { depth: 0.25, color: 0xf5f5f5, roughness: 0.7, metalness: 0 }
      if (/gator/i.test(sub)) return { depth: 0.125, color: 0x5a5a5a, roughness: 0.6, metalness: 0, hasSides: true }
      if (/mural/i.test(sub)) return { depth: 0.1, color: 0xffffff, roughness: 0.9, metalness: 0 }
      if (/window\s*film/i.test(sub)) {
        const material = (getOptionValue(/material/i) || getOptionValue(/window\s*film/i) || '').toLowerCase()
        const isOpaque = /opaque/i.test(material)
        const isTransparent = /transparent/i.test(material)
        const isTranslucent = /translucent/i.test(material)
        const isPerforated = /perforat/i.test(material)
        if (isOpaque) return { depth: 0.1, color: 0xffffff, roughness: 0.9, metalness: 0 }
        return {
          depth: 0,
          useBackface: isTransparent || isTranslucent || isPerforated,
          opacity: isTransparent ? 0.65 : isTranslucent ? 0.9 : 1,
          perforated: isPerforated,
          holesPerInch: 4,
          holeSizeInches: 0.0625,
        }
      }
      return null
    },
    /** Hang hardware data */
    computedHangData() {
      for (const [category, optionId] of Object.entries(this.selectedOptions)) {
        if (!optionId) continue
        if (!/hang/i.test(category)) continue
        const opt = this.availableOptions.find(o => o.id === optionId)
        if (opt) {
          const name = this.getOptionLabel(opt)
          return { name }
        }
      }
      return null
    },
    /** Full frame configuration object emitted via frame-config-changed */
    frameConfig() {
      return {
        substrate: this.currentSubstrateName,
        frameData: this.computedFrameData,
        matData: this.computedMatData,
        matDataArray: this.computedMatDataArray,
        matWidth: this.computedMatWidth,
        matWidthsArray: this.computedMatWidthsArray,
        hangData: this.computedHangData,
        stretcherDepth: this.computedStretcherDepth,
        canvasMount: this.computedCanvasMount,
        floatFrameData: this.computedFloatFrameData,
        substrateBackData: this.computedSubstrateBackData,
      }
    },
  },
  watch: {
    frameConfig: {
      handler(val) {
        this.$emit('frame-config-changed', val)
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    isFrameCategory(category) {
      return category && /frame/i.test(category)
    },
    getSubcategories(options) {
      const groups = new Set()
      options.forEach(opt => {
        const cGroups = opt.extra_data && opt.extra_data.color_groups
        if (Array.isArray(cGroups)) {
          cGroups.forEach(g => groups.add(g))
        }
      })
      return Array.from(groups).sort().map(g => ({ value: g, text: g }))
    },
    getCategoryOptionsFiltered(options, category) {
      const selectedSub = this.selectedSubcategories[category]
      let filteredOptions = options
      if (selectedSub) {
        filteredOptions = options.filter(opt => {
          const cGroups = opt.extra_data && opt.extra_data.color_groups
          return Array.isArray(cGroups) && cGroups.includes(selectedSub)
        })
      }
      return filteredOptions.map(opt => ({
        value: opt.id,
        text: this.getOptionLabel(opt),
      }))
    },
    getOptionLabel(opt) {
      const val = opt.value
      if (val === null || val === undefined || val === '') return opt.name
      if (typeof val === 'number') return String(val)
      if (typeof val === 'boolean') return val ? 'Yes' : 'No'
      return String(val)
    },
    getFieldError(...keys) {
      for (const key of keys) {
        if (this.validationErrors[key]) return this.validationErrors[key]
      }
      return null
    },
    getOptionError(category) {
      const prefix = `config_data.${category}.`
      const msgs = []
      Object.entries(this.validationErrors).forEach(([key, msg]) => {
        if (key.startsWith(prefix) || key === `config_data.${category}`) {
          msgs.push(msg)
        }
      })
      return msgs.length ? msgs.join(', ') : null
    },
    updateDimension(key, value) {
      this.$emit('update:dimensions', { ...this.dimensions, [key]: value })
    },
    updateOption(category, value) {
      this.$emit('update:selectedOptions', { ...this.selectedOptions, [category]: value })
    },
    updateSubcategory(category, value) {
      this.$emit('update:selectedSubcategories', { ...this.selectedSubcategories, [category]: value })
    },
    onSubstrateChange(value) {
      this.$emit('update:selectedSubstrate', value)
      this.$emit('substrate-changed', value)
    },
  },
}
</script>

<style scoped>
.ds-field {
  display: flex;
  align-items: baseline;
  margin-bottom: 0.35rem;
}

.ds-label {
  flex: 0 0 auto;
  width: 68px;
  min-width: 68px;
  font-size: 0.7rem;
  color: #6c757d;
  margin-bottom: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 0.35rem;
  line-height: 1.8;
  text-transform: capitalize;
}

.ds-input {
  flex: 1 1 auto;
  min-width: 0;
}

.ds-sidebar-form hr {
  margin: 0.4rem 0;
}

.ds-sidebar-form .btn-sm {
  font-size: 0.72rem;
  padding: 0.2rem 0.5rem;
}

.ds-sidebar-form >>> .custom-select-sm,
.ds-sidebar-form >>> .form-control-sm {
  font-size: 0.75rem;
  padding: 0.15rem 0.4rem;
  height: calc(1.5em + 0.3rem + 2px);
}

.ds-error {
  font-size: 0.68rem;
  color: #dc3545;
  margin-top: 0.1rem;
  line-height: 1.3;
}

.ds-frame-images {
  display: flex;
  align-items: flex-start;
  margin-bottom: 0.35rem;
}

.ds-frame-images .ds-label {
  flex: 0 0 auto;
}

.ds-frame-img {
  text-align: center;
}

.ds-frame-img img {
  max-width: 80px;
  max-height: 48px;
  object-fit: contain;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.ds-frame-img small {
  font-size: 0.6rem;
}
</style>
